<script setup>
/**
 * Lottie animation with the API of the former visivalab-components VLottie.
 *
 * Only the light SVG player is loaded, and only once the component mounts: no
 * page start pays for a map library to animate an icon, and SSR renders the
 * same empty container that hydration finds. Calls made before the player has
 * loaded are applied, in order, as soon as the animation exists.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { loadLottiePlayer } from '@/runtime/lottiePlayer';

defineOptions({ name: 'VLottie' });

const props = defineProps({
    loop: { type: Boolean, default: false },
    autoplay: { type: Boolean, default: false },
    src: { type: Object, default: null },
    path: { type: String, default: null },
    speed: { type: Number, default: 1 },
});
const emit = defineEmits(['ready']);

const container = ref(null);
let animation = null;
let pending = [];
let disposed = false;

onMounted(async () => {
    let lottie;
    try {
        lottie = await loadLottiePlayer();
    } catch {
        return;
    }
    if (disposed || !container.value) return;
    animation = lottie.loadAnimation({
        container: container.value,
        renderer: 'svg',
        loop: props.loop,
        autoplay: props.autoplay,
        ...(props.src ? { animationData: props.src } : { path: props.path }),
    });
    animation.setSpeed(props.speed);
    const queued = pending;
    pending = [];
    queued.forEach(([method, args]) => animation[method](...args));
    const ready = () => { if (!disposed) emit('ready'); };
    if (animation.isLoaded) ready();
    else animation.addEventListener('DOMLoaded', ready);
});

onBeforeUnmount(() => {
    disposed = true;
    pending = [];
    animation?.destroy();
    animation = null;
});

const control = method => (...args) => {
    if (animation) animation[method](...args);
    else if (!disposed) pending.push([method, args]);
};

defineExpose({
    play: control('play'),
    stop: control('stop'),
    pause: control('pause'),
    playSegments: control('playSegments'),
    setSpeed: control('setSpeed'),
    goToAndStop: control('goToAndStop'),
    goToAndPlay: control('goToAndPlay'),
    setDirection: control('setDirection'),
    destroy: () => {
        pending = [];
        animation?.destroy();
        animation = null;
    },
});
</script>

<template>
    <div ref="container" class="v-lottie" />
</template>
